import { ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import HeaderController from "../../components/Headers/HeaderController";
import LoadingModal from "../../components/Modals/LoadingModal/LoadingModal";
import ServerError from "../../components/ServerError/ServerError";
import StickyNotification from "../../components/StickyNotification/StickyNotification";
import AnimatedPopup from "../../components/AnimatedPopup/AnimatedPopup";
import { TrustBadgeSection } from "../../components/Modals/SideMenu/components/TrustBadgeSection";
import { translate } from "../../utils/translator";
import * as http from "../../utils/http";
import { useAppContext } from "../../providers/AppProvider";
import { MainTracker } from "../../PixelTrackers/MainTracker";
import { getWlExperimentTrackingProps, registerWlExperimentSuperProperties } from "../../utils/wlCarePlusExperiment";
import {
  CHOOSE_PLAN_LINK_TARGET,
  CHOOSE_PLAN_ORDER_PROPERTY,
  CHOOSE_PLAN_SECTION_ORDER,
  ChoosePlanOrderVariant,
  ChoosePlanSection,
  assignChoosePlanOrderVariant,
} from "../../utils/choosePlanOrderExperiment";
import { CarePlusMedicationLine, rememberServiceFeeWlArm, useChoosePlanCarePlus } from "../ChoosePlanCarePlus/useChoosePlanCarePlus";
import { CarePlusPlanDuration, CarePlusPlanTier } from "../ChoosePlanCarePlus/types";
import { CarePlusDosageOption } from "../ChooseTreatmentCarePlus/types";
import CarePlusHeaderBar from "../CarePlusShared/CarePlusHeaderBar";
import HelpBlock from "../ChooseTreatmentCarePlusNew/components/HelpBlock";
import { medicationHeroImageClassName } from "../ChooseTreatmentCarePlusNew/helpers";
import { CAT, GRADIENT, HERO_GRADIENT, TEXT_GRADIENT } from "./constants";
import { hasOneTimeDiscount, hasPlanDiscount, isPriced } from "./pricing";
import MonthlyPlanCard from "./components/MonthlyPlanCard";
import OneTimeOrderCard from "./components/OneTimeOrderCard";
import PrescriptionOnlyRow from "./components/PrescriptionOnlyRow";
import DosageOverview from "./components/DosageOverview";
import { doseProductName, getDoseNoticeCartExperiments } from "../ChooseTreatmentCarePlusNew/doseNoticeVisit";
import ExpandableSection from "../ChooseTreatmentCarePlus/components/ExpandableSection";

// Property name as in the HL-11888 ticket.
const SERVICE_FEE_WL_SUPER_PROPERTY = "service fee WL test";

// V3 Choose Plan. Same data + persistence as ChoosePlanCarePlus
// (useChoosePlanCarePlus is shared untouched); only the presentation differs: one Monthly card,
// then One Time / Prescription only as separate lines, no Pro/Max.

// Dosage-help copy lives in the Choose Treatment category — the "Learn more" popup reuses it.
const CT_CAT = "choose_treatment_care_plus";
// HL-12122: V3 sells the Monthly plan, which has its own plan rows (tier "monthly").
const MONTHLY_TIER = "monthly";
// HL-12001 / HL-12264: markets in the Choose Plan order A/B/C test.
const CHOOSE_PLAN_ORDER_COUNTRIES = ["de", "gb"];

interface Props {
  initialMedication?: CarePlusMedicationLine | null;
  /** The medication's full dosage list, for the price & dosage table. Carried in from Choose
   * Treatment; the cart line alone only knows the one picked. */
  dosages?: CarePlusDosageOption[];
  /** Header back. The in-page CT → Plan swap never pushed history, so the host passes a handler
   * that swaps back to Choose Treatment; a real mount falls back to browser history. */
  onBack?: () => void;
}

// The card is a monthly plan: the 1-month row when the tier has one, else its shortest.
const monthlyDurationFor = (tier: CarePlusPlanTier | null): CarePlusPlanDuration | null =>
  tier?.durations.find((d) => d.period_months === 1) ?? tier?.durations[0] ?? null;

const ChoosePlanCarePlusNew = ({ initialMedication, dosages = [], onBack }: Props) => {
  const { country, language, category } = useParams() as { country: string; language: string; category: string };
  const { headerVersion } = useAppContext();
  const navigate = useNavigate();
  // HL-12001: the "Not ready to subscribe?" link scrolls to one of these, per arm.
  const oneTimeRef = useRef<HTMLDivElement>(null);
  const prescriptionOnlyRef = useRef<HTMLDivElement>(null);
  // HL-12001: section-order arm. Null until the plans (and their weights) have arrived, so the
  // page never paints in one order and re-flows into another.
  const [variant, setVariant] = useState<ChoosePlanOrderVariant | null>(null);
  const [showDosageHelp, setShowDosageHelp] = useState(false);
  // The product write that precedes the plan write on Continue (see persistMedicationToCart).
  const [persistingMedication, setPersistingMedication] = useState(false);
  const [medicationError, setMedicationError] = useState(false);

  const {
    loading,
    error,
    tiers,
    allowOneTimePremium,
    showMedicationInPlanPrice,
    productFees,
    oneTimeBonus,
    choosePlanOrderABTestWeights,
    serviceFeeWlTest,
    selectTier,
    selectDuration,
    continueToCheckout,
    medication,
    submitting,
    submitError,
    dismissSubmitError,
  } = useChoosePlanCarePlus(initialMedication, { skipViewTracking: true });

  // V3 sells one plan: the monthly tier. The WL2 Care+ tiers (basic / pro / max) are never sold here.
  const monthlyTier = tiers.find((tier) => tier.tier === MONTHLY_TIER) ?? null;
  const monthlyDuration = monthlyDurationFor(monthlyTier);
  const hasPlan = monthlyTier !== null && monthlyDuration !== null;

  // Non-subscription pricing. One Time is medication + all three
  // fees (struck through over what the one-time bonus leaves); Prescription only is its own fee.
  // Computed before the loading guard below so the default-selection effect can use them.
  const treatmentFee = Number(productFees?.treatment_fee ?? 0);
  const serviceFee = Number(productFees?.service_fee ?? 0);
  const deliveryFee = Number(productFees?.shipping_fee ?? 0);
  const medicationOfficialPrice = medication?.official_price ?? medication?.item_price ?? 0;
  const oneTimePrice = medicationOfficialPrice + treatmentFee + serviceFee + deliveryFee;
  const oneTimeChargedPrice = Math.max(0, oneTimePrice - Number(oneTimeBonus?.discount ?? 0));
  const prescriptionOnlyPrice = Number(productFees?.prescription_only_treatment_fee ?? 0) || treatmentFee;

  // An option is only offered when every number behind its price is known (see isPriced). The
  // medication is common to Monthly and One Time; Prescription only is just its own fee.
  const medicationPriced = isPriced(medicationOfficialPrice);
  const canOfferMonthly = hasPlan && medicationPriced && isPriced(monthlyDuration!.total_fee);
  // UK: a single fee may genuinely be 0 (no treatment fee, free delivery), so only their sum must be set.
  // Elsewhere a 0 fee means it is missing (the matrix coerces null to 0), so every fee must be set.
  const isUK = country?.toUpperCase() === "GB";
  const oneTimeFeesPriced = isUK ? isPriced(treatmentFee + serviceFee + deliveryFee) : isPriced(treatmentFee, serviceFee, deliveryFee);
  const canOfferOneTime = allowOneTimePremium && medicationPriced && oneTimeFeesPriced;
  // Not offered in the UK: UK shipping has no prescription-only path (DeliveryOptionPanel), so
  // the order could not be completed. Remove the country check once compliance approves it.
  const canOfferPrescriptionOnly = !isUK && isPriced(prescriptionOnlyPrice);

  // Monthly is the default (§8). When it is not on offer the page falls through to whichever
  // non-subscription option is — otherwise the CTA would sit there doing nothing
  // (continueToCheckout bails without a duration).
  const [isOneTime, setIsOneTime] = useState(false);
  const [isPrescriptionOnly, setIsPrescriptionOnly] = useState(false);
  // Applied once the plans have arrived, not at mount: `tiers` is empty on the first render, so
  // seeding the state there would pre-select One Time on every product and never correct itself
  // (a useState initialiser runs once). The ref keeps a later customer choice.
  const defaultsApplied = useRef(false);
  useEffect(() => {
    if (loading || error || defaultsApplied.current) return;
    defaultsApplied.current = true;
    if (canOfferMonthly) return;
    setIsOneTime(true);
    setIsPrescriptionOnly(!canOfferOneTime && canOfferPrescriptionOnly);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, canOfferMonthly, canOfferOneTime]);

  const t = (key: string, fallback: string) => translate(language, CAT, key) ?? fallback;
  const tCT = (key: string, fallback: string) => translate(language, CT_CAT, key) ?? fallback;

  // Once the plan matrix has arrived, log what the server sent so a card that renders wrong
  // (missing base tier, no 1-month row, unexpected bonus) can be traced to the payload.
  useEffect(() => {
    if (loading || error) return;
    window.clientLogger.info("Choose Plan V3: tiers received from server", {
      tiers,
      monthlyTier: monthlyTier?.tier ?? null,
      monthlyDuration,
      allowOneTimePremium,
      showMedicationInPlanPrice,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, tiers]);

  // HL-12001: bucket once the weights are here (sticky per browser), then fire the view event
  // with the arm. The audience is DE and (HL-12264) the UK — every other country gets the control
  // order and no assignment.
  const isChoosePlanOrderCountry = CHOOSE_PLAN_ORDER_COUNTRIES.includes(country.toLowerCase());
  useEffect(() => {
    if (loading || error || variant) return;
    const arm = isChoosePlanOrderCountry ? assignChoosePlanOrderVariant(choosePlanOrderABTestWeights) : "A";
    setVariant(arm);
    // HL-12122: the shared page view, fired here (not in the hook) so it carries the assigned arm.
    registerWlExperimentSuperProperties();
    MainTracker.track("pv", "View Choose Plan Page", {
      mention: category,
      ...getWlExperimentTrackingProps(),
      ...(isChoosePlanOrderCountry ? { choose_plan_variant: arm.toLowerCase() } : {}),
    });
    MainTracker.track("custom", "choose_plan_viewed", {
      variant: arm,
      product: medication?.title,
      product_item_id: medication?.product_item_id,
      mention: category,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error]);

  // Fire in both arms so A has a denominator; Paid Order reads the arm from the cart.
  useEffect(() => {
    if (!variant || !serviceFeeWlTest || !hasPlan) return;
    MainTracker.superProperties({ [SERVICE_FEE_WL_SUPER_PROPERTY]: serviceFeeWlTest });
    MainTracker.people.set({ [SERVICE_FEE_WL_SUPER_PROPERTY]: serviceFeeWlTest });
    if (!rememberServiceFeeWlArm(serviceFeeWlTest)) return;
    MainTracker.track("custom", "Service fee test exposed", {
      [SERVICE_FEE_WL_SUPER_PROPERTY]: serviceFeeWlTest,
      service_fee_amount: monthlyDuration?.bonus?.fully_covered_fees?.includes("service_fee") ? 0 : monthlyDuration?.fee_breakdown?.service_fee ?? 0,
      product: medication?.title,
      country,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, serviceFeeWlTest]);

  if (error) {
    return (
      <>
        <HeaderController showSteps={false} stage={"Treatment"} headerVersion={headerVersion} />
        <ServerError status={500} />
      </>
    );
  }

  if (loading || !variant) return <LoadingModal />;

  // Every option was withheld for want of a price. Rendering an empty page with a dead CTA would
  // hide a data problem, so fail the same way a failed request does.
  if (!canOfferMonthly && !canOfferOneTime && !canOfferPrescriptionOnly) {
    window.clientLogger.error("Choose Plan V3: no option can be priced", {
      productFees,
      medicationOfficialPrice,
      monthlyDuration,
    });
    return (
      <>
        <HeaderController showSteps={false} stage={"Treatment"} headerVersion={headerVersion} />
        <ServerError status={500} />
      </>
    );
  }

  const isMonthlySelected = canOfferMonthly && !isOneTime && !isPrescriptionOnly;
  // Prescription only also sets isOneTime, so it is checked first.
  const selectedPlanName = isPrescriptionOnly
    ? t("tier_prescription_only", "Prescription only")
    : isOneTime
      ? t("tier_one_time_order", "One Time order")
      : t("plan_monthly", "Monthly");

  // The anchor note: shown only while some price on the page is crossed out — the Monthly
  // card (plan bonus) or the One Time row (one-time bonus). Once the sale ends neither is, so the
  // note disappears with them. Both predicates come from pricing.ts, which is also what the two
  // cards strike a price through on, so the note and the strike-through cannot disagree. Gated on
  // the same canOffer flags, so a withheld option cannot leave the note behind on its own.
  const showSavingsNote =
    (canOfferMonthly && hasPlanDiscount(monthlyDuration)) ||
    (canOfferOneTime && hasOneTimeDiscount(oneTimePrice, oneTimeChargedPrice));

  const selectMonthly = () => {
    if (!monthlyTier || !monthlyDuration) return;
    setIsOneTime(false);
    setIsPrescriptionOnly(false);
    selectTier(monthlyTier.tier);
    selectDuration(monthlyDuration.period_months);
  };
  const selectOneTime = () => {
    setIsOneTime(true);
    setIsPrescriptionOnly(false);
  };
  const selectPrescriptionOnly = () => {
    setIsOneTime(true);
    setIsPrescriptionOnly(true);
  };

  // V2 writes the cart in two submits — Choose Treatment posts the product, Choose Plan
  // posts the plan. The V3 handoff is an in-page swap that never posted the product, so this page
  // does both on its one Continue, in that order: cart/update_subscription_data validates the plan
  // against cart.product_item_id and rejects it (product_subscription_not_found) on an empty cart.
  // Same endpoint and payload Choose Treatment V2 uses (useChooseTreatmentCarePlus), so the cart
  // row and Shipping are identical to the V2 arm's.
  const persistMedicationToCart = () => {
    if (!medication?.product_item_id || !medication.catalog_id) {
      return Promise.reject(new Error("care_plus_v3_no_medication_to_persist"));
    }
    // Out of stock and not pre-orderable → the product is written as prescription-only, exactly as
    // Choose Treatment V2 does. Distinct from the customer choosing "Prescription only" below, which
    // the plan write carries as onlyPrescription.
    const outOfStockRxOnly = medication.product_in_stock === false && !medication.is_pre_orderable;
    return http.postToServer(
      "choose-treatment-free-dosage",
      {
        cartProductsDTOs: [
          {
            quantity: 1,
            product_in_stock: !outOfStockRxOnly,
            only_prescription: outOfStockRxOnly,
            catalog_id: medication.catalog_id,
            product_item_id: medication.product_item_id,
            is_pre_selected: false,
          },
        ],
        // HL-12001 AC-16: persisted on the cart (saveCartExperiments) and spread onto the
        // server-side Paid Order event by the offline pixels consumer.
        // HL-12999: dose_notice_shown + selected_product, same way (UK WL only).
        experiments: {
          [CHOOSE_PLAN_ORDER_PROPERTY]: variant,
          ...getDoseNoticeCartExperiments(category, doseProductName(medication.title, medication.dosage_str)),
        },
      },
      [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER],
    );
  };

  // The hook pre-selects the tier with its own default period (3 months). The card here is the
  // 1-month row, so the plan is named explicitly instead of trusting that state.
  const onContinue = async () => {
    if (submitting || persistingMedication) return;
    setMedicationError(false);
    setPersistingMedication(true);
    try {
      await persistMedicationToCart();
    } catch (error) {
      // The product never reached the cart, so the plan write would be rejected and Shipping would
      // open empty. Stop here, tell the customer, release the CTA.
      window.clientLogger.warn("Choose Plan V3: persisting the medication failed", error);
      setPersistingMedication(false);
      setMedicationError(true);
      return;
    }
    setPersistingMedication(false);
    continueToCheckout(
      isOneTime
        ? { oneTime: true, prescriptionOnly: isPrescriptionOnly }
        : { oneTime: false, prescriptionOnly: false, tier: monthlyTier?.tier, periodMonths: monthlyDuration?.period_months },
    );
  };
  const busy = submitting || persistingMedication;

  // Scroll only — never selects (AC-13). Falls back to whichever non-subscription option is on
  // the page when the arm's target was withheld for want of a price.
  const linkTarget = CHOOSE_PLAN_LINK_TARGET[variant];
  const scrollTarget: ChoosePlanSection | null = !linkTarget
    ? null
    : linkTarget === "prescription_only" && canOfferPrescriptionOnly
      ? "prescription_only"
      : canOfferOneTime
        ? "one_time"
        : canOfferPrescriptionOnly
          ? "prescription_only"
          : null;
  // HL-12894: the link only earns its place when the trust badges / dosage overview sit between it
  // and the option it scrolls to. UK arm B withholds Prescription only, so the link fell back to
  // the One Time card directly below it and just pushed that card to the top edge.
  const sectionOrder = CHOOSE_PLAN_SECTION_ORDER[variant];
  const showNotReadyLink =
    scrollTarget !== null && sectionOrder.slice(0, sectionOrder.indexOf(scrollTarget)).some((s) => s === "trust" || s === "dosage");
  const scrollToOptions = () => {
    const ref = scrollTarget === "prescription_only" ? prescriptionOnlyRef : oneTimeRef;
    ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // faq_q2 stays in the translation file because the Care+ shipping FAQ still renders it.
  const faq = [
    { key: "included", q: t("faq_included_q", "What's included in the subscription?"), a: t("faq_included_a", "") },
    { key: "total", q: t("faq_q3", "Will my total increase?"), a: t("faq_a3", "") },
    { key: "not_approved", q: t("faq_q4", "What happens if a prescription is not approved?"), a: t("faq_a4", "") },
  ];

  const groupNonSubscription = variant === "A";
  // Follows the link's real target, so UK arm B (no link) still gets the line over its One Time card.
  const notReadyHeading = (section: ChoosePlanSection) =>
    scrollTarget === section && canOfferMonthly ? <p className="text-[12px] text-[#8a9099]">{t("not_ready_to_subscribe", "Not ready to subscribe?")}</p> : null;
  // HL-12035 2.6: when grouped under the One Time card (arm A), twice the section gap between them.
  const prescriptionOnlySection = canOfferPrescriptionOnly ? (
    <div
      key="prescription_only"
      ref={prescriptionOnlyRef}
      data-testid="care-plus-v3-section-prescription-only"
      className={`flex flex-col gap-[12px] ${groupNonSubscription && canOfferOneTime ? "mt-[12px]" : ""}`}
    >
      {notReadyHeading("prescription_only")}
      <PrescriptionOnlyRow t={t} price={prescriptionOnlyPrice} isSelected={isPrescriptionOnly} onSelect={selectPrescriptionOnly} />
    </div>
  ) : null;
  const sections: Record<ChoosePlanSection, ReactNode> = {
    trust: <TrustBadgeSection key="trust" className="mx-auto w-fit bg-white" language={language} country={country} />,
    dosage: (
      <DosageOverview
        key="dosage"
        t={t}
        medicationTitle={medication?.title ?? ""}
        dosages={dosages}
        selectedProductItemId={medication?.product_item_id ?? null}
        onLearnMore={() => setShowDosageHelp(true)}
        showSavingsNote={showSavingsNote}
        isTabletProduct={showMedicationInPlanPrice}
        packLabel={dosages.find((d) => d.product_item_id === medication?.product_item_id)?.quantity_title || medication?.quantityStr || ""}
      />
    ),
    one_time: (
      <div key="one_time" className="flex flex-col gap-[12px]" data-testid="care-plus-v3-section-one-time">
        {notReadyHeading("one_time")}
        {canOfferOneTime && (
          <div ref={oneTimeRef}>
            <OneTimeOrderCard
              t={t}
              medication={medication}
              price={oneTimePrice}
              chargedPrice={oneTimeChargedPrice}
              isSelected={isOneTime && !isPrescriptionOnly}
              onSelect={selectOneTime}
            />
          </div>
        )}
        {groupNonSubscription && prescriptionOnlySection}
      </div>
    ),
    prescription_only: groupNonSubscription ? null : prescriptionOnlySection,
  };

  return (
    <>
      {/* Persisting the medication or the plan failed, so the customer was NOT sent on to Shipping.
          Tell them and let them retry. */}
      <StickyNotification
        showNotification={submitError || medicationError}
        level="error"
        title={translate(language, "default", "StickyNotification/generic_server_error_title") || "Server error"}
        content={t("submit_failed", "Something went wrong. Please try again later.")}
        onClick={() => {
          dismissSubmitError();
          setMedicationError(false);
        }}
      />

      <div id="wl3_plan_page" className="relative min-h-screen bg-white pb-28">
        {/* Full-bleed hero: orange → white at 80% over white. */}
        <div className="absolute left-0 top-0 h-[470px] w-full opacity-80 md:h-[587px]" style={{ background: HERO_GRADIENT }} />

        <div className="relative z-10 mx-auto max-w-[520px] px-[14px] md:px-0">
          {/* Hero: stepper, title, medication image + name */}
          <div className="relative flex flex-col items-center pt-[74px]">
            <CarePlusHeaderBar backId="wl3_plan_back" current="plan" onBack={onBack ?? (() => navigate(-1))} t={t} />
            <h1 className="max-w-[454px] text-center font-poppins text-[26px] font-semibold leading-[1.2] text-white md:text-[32px] md:leading-[38px]">
              {t("v3_title", "Choose how you want to receive your medication")}
            </h1>
            <div className="flex h-[160px] w-full items-center justify-center">
              {medication?.imageSrc && (
                <img src={medication.imageSrc} alt={medication.title} className={medicationHeroImageClassName(medication.title)} />
              )}
            </div>
            {medication?.title && (
              <p data-testid="care-plus-v3-medication-name" className="my-[9px] text-center text-[22px] font-medium leading-[32px] text-[#08080a]">
                {medication.title}
              </p>
            )}
          </div>

          <div className="mt-[60px] flex flex-col gap-[36px]">
            {/* Monthly plan card + "Not ready to subscribe?" jump link (arms A and B only) */}
            {canOfferMonthly && (
              <div className="flex flex-col items-center gap-[18px]">
                <MonthlyPlanCard
                  t={t}
                  duration={monthlyDuration}
                  medication={medication}
                  isSelected={isMonthlySelected}
                  onSelect={selectMonthly}
                />
                {/* A link only where there is somewhere to scroll to. In C (and UK B) the option
                    sits right below, so there is no line at all. */}
                {showNotReadyLink && (
                  <button
                    id="wl3_plan_not_ready"
                    type="button"
                    data-testid="care-plus-v3-not-ready-link"
                    onClick={scrollToOptions}
                    className="cursor-pointer border-0 bg-transparent p-0 text-[12px] text-[#8a9099] underline"
                  >
                    {t("not_ready_to_subscribe_link", "Not ready to subscribe? – for more purchase options")}
                  </button>
                )}
              </div>
            )}

            {/* HL-12357: 1.5x the gap under the dosage overview's savings note (36 → 54px). */}
            {CHOOSE_PLAN_SECTION_ORDER[variant].map((section, i, order) =>
              order[i - 1] === "dosage" && sections[section] ? (
                <div key={section} className="mt-[18px]">
                  {sections[section]}
                </div>
              ) : (
                sections[section]
              ),
            )}
          </div>

          {/* FAQ — HL-12357: 1.5x the gap above it (32 → 48px). */}
          <div data-testid="care-plus-v3-faq" className="mt-[48px] rounded-2xl bg-[#f5f6f7] px-5 py-6">
            <div className="flex flex-col items-center gap-3 text-center">
              <p className="text-[10px] text-[#616975]">{tCT("faq_eyebrow", "FREQUENTLY ASKED QUESTIONS")}</p>
              <div className="w-[278px] text-[24px] font-medium leading-[32px] text-black">
                <p>{tCT("faq_title", "Do you have any further questions? We have the answers")}</p>
              </div>
            </div>
            <div className="mt-10">
              {faq
                .filter((item) => item.q || item.a)
                .map((item) => (
                  <ExpandableSection key={item.q} id={`wl3_plan_faq_${item.key}`} title={item.q} bodyHtml={item.a} />
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky CTA — same chrome as Choose Treatment V3. Not in the Figma frame; kept because the
          flow needs a way to continue. */}
      <div className="fixed bottom-0 left-0 right-0 z-40 mx-auto w-full max-w-[520px] px-4 pb-6">
        <button
          id="wl3_plan_continue"
          type="button"
          data-testid="care-plus-continue-cta"
          disabled={busy}
          aria-busy={busy}
          onClick={onContinue}
          className={`flex h-[50px] w-full items-center justify-center rounded-[12px] border-0 text-[14px] font-medium text-white drop-shadow-[0px_4px_3px_rgba(232,176,33,0.2)] ${
            busy ? "cursor-default opacity-70" : "cursor-pointer hover:opacity-90 active:scale-[0.98]"
          }`}
          style={{ background: GRADIENT }}
        >
          {busy ? (
            <span
              data-testid="care-plus-continue-spinner"
              aria-label={t("submitting", "Saving your choice")}
              className="size-[24px] animate-spin rounded-full border-2 border-solid border-white/40 border-t-white"
            />
          ) : (
            // One inline box like Choose Treatment; as separate flex items the space before the plan name collapses.
            <span>
              {tCT("continue_cta", "Continue with")} <span className="font-semibold">{selectedPlanName}</span>
            </span>
          )}
        </button>
      </div>

      {/* "Learn more" — the dosage guidance Choose Treatment V3 already shows, not new copy. */}
      <AnimatedPopup
        isVisible={showDosageHelp}
        onClose={() => setShowDosageHelp(false)}
        closeButtonId="wl3_plan_dosage_help_popup_close"
        title=""
        closeOnEscape
        centered
        containerClassName="!max-w-[354px] !rounded-[16px]"
      >
        <h2 id="wl3_plan_dosage_help_popup" className="mb-[24px] text-[21px] font-medium leading-[32px] text-black">
          {tCT("dosage_help_v3_how", "How to")}{" "}
          <span className="bg-clip-text text-transparent" style={{ backgroundImage: TEXT_GRADIENT }}>
            {tCT("dosage_help_v3_choose", "choose your dosage")}
          </span>
        </h2>
        <div className="flex flex-col gap-[20px]">
          {[
            ["dosage_help_v3_start_title", "dosage_help_v3_start_body", "Starting treatment", "We recommend starting on the lowest dose. The dose is then increased slowly as your body gets used to the medication."],
            ["dosage_help_v3_continue_title", "dosage_help_v3_continue_body", "Continuing treatment", "If you are taking this medication now, or took it in the last month, choose the dose you are currently taking."],
            ["dosage_help_v3_restart_title", "dosage_help_v3_restart_body", "Restarting treatment", "If your last dose was more than 2 months ago, it is best to restart on a low dose."],
          ].map(([titleKey, bodyKey, titleFallback, bodyFallback]) => (
            <HelpBlock key={titleKey} title={tCT(titleKey, titleFallback)} body={tCT(bodyKey, bodyFallback)} />
          ))}
        </div>
      </AnimatedPopup>
    </>
  );
};

export default ChoosePlanCarePlusNew;
